import { Mail, Sparkles } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { motion } from 'framer-motion';

const EmailComposerSection = () => {
  const isMobile = useIsMobile();
  
  return (
    <section className="section relative py-24 overflow-hidden">
      {/* Background gradient effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/10 to-transparent" />
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="max-w-6xl mx-auto px-4 sm:px-6 relative"
      >
        <div className="flex flex-col space-y-8">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Feature badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-pink-500/10 backdrop-blur-sm border border-purple-500/20">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                AI-Powered Composer
              </span>
            </div>
            
            {/* Icon with glow effect */}
            <motion.div 
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.5, type: "spring" }}
              viewport={{ once: true }}
              className="relative inline-block"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 blur-xl opacity-50" />
              <div className="relative bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-sm border border-purple-500/30 p-4 rounded-2xl">
                <Mail size={isMobile ? 28 : 32} className="text-white" />
              </div>
            </motion.div>
            
            <h3 className="text-3xl md:text-4xl font-bold">
              <span className="text-white">Email Composer</span>{' '}
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent animate-gradient-x">
                To Launch
              </span>
            </h3>
            
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">
              Got inspired? Use Email Composer to{' '}
              <span className="text-white font-medium">create emails and one click launch</span>{' '}
              new campaigns or add them to existing campaigns.
            </p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Glow effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-purple-500/20 to-pink-500/20 blur-3xl opacity-60" />
            
            {/* Glass morphism video container */}
            <div className="relative backdrop-blur-sm bg-gradient-to-br from-gray-800/40 to-gray-900/40 rounded-2xl border border-gray-700/50 p-1 shadow-2xl hover:shadow-purple-500/20 transition-all duration-300">
              <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 rounded-xl overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-tr from-purple-500/5 to-pink-500/5" />
                <AspectRatio ratio={16/9} className="w-full relative">
                  <iframe 
                    src="https://player.vimeo.com/video/1069190292?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479"
                    width="100%" 
                    height="100%" 
                    frameBorder="0" 
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                    title="Email Composer"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                  />
                </AspectRatio>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default EmailComposerSection;
